import type {
  FollowMutationResponse,
  FollowVisitRequest,
  FollowVisitResponse,
  FollowsResponse,
} from "@skills-marketplace/contracts/contract-11-follows";
import { authHeaders, baseUrl, handleResponse, proxyFetch } from "./github-proxy-client";

/**
 * Client for the follow API (Contract 11, product-management #665).
 *
 * Same split as feedback-client: reads never throw (a failed list shows its empty or
 * retry state), writes throw so the caller can roll back an optimistic toggle.
 */

/** The caller's follows with change flags, or null when unavailable. */
export async function fetchFollows(): Promise<FollowsResponse | null> {
  try {
    const headers = await authHeaders();
    const response = await proxyFetch(`${baseUrl()}/follows`, { method: "GET", headers });
    if (!response.ok) {
      console.warn(`[follow] follows unavailable (${response.status})`);
      return null;
    }
    const body = (await response.json()) as FollowsResponse;
    if (!body || typeof body !== "object" || !Array.isArray(body.items)) {
      console.warn("[follow] follows response was not the expected shape");
      return null;
    }
    if (typeof body.schemaVersion === "string" && !body.schemaVersion.startsWith("1.")) {
      console.warn(`[follow] follows schemaVersion ${body.schemaVersion} is not compatible`);
      return null;
    }
    return body;
  } catch (err) {
    console.warn("[follow] follows could not be fetched", err);
    return null;
  }
}

/** Follow a catalog item. Throws on failure (409 at the follow limit). */
export async function followItem(installName: string): Promise<FollowMutationResponse> {
  const headers = await authHeaders();
  const response = await proxyFetch(`${baseUrl()}/follows/${encodeURIComponent(installName)}`, {
    method: "PUT",
    headers,
  });
  return handleResponse<FollowMutationResponse>(response);
}

/** Unfollow a catalog item. Throws on failure. */
export async function unfollowItem(installName: string): Promise<FollowMutationResponse> {
  const headers = await authHeaders();
  const response = await proxyFetch(`${baseUrl()}/follows/${encodeURIComponent(installName)}`, {
    method: "DELETE",
    headers,
  });
  return handleResponse<FollowMutationResponse>(response);
}

/**
 * Record that the Following panel was shown, echoing the asOf and familySnapshot from the
 * GET so the cursor advances to what the user saw. Silent on failure: the flags simply
 * show again next time.
 */
export async function postVisit(request: FollowVisitRequest): Promise<FollowVisitResponse | null> {
  try {
    const headers = await authHeaders();
    const response = await proxyFetch(`${baseUrl()}/follows/visit`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      console.warn(`[follow] visit not recorded (${response.status})`);
      return null;
    }
    return (await response.json()) as FollowVisitResponse;
  } catch (err) {
    console.warn("[follow] visit could not be recorded", err);
    return null;
  }
}
